/**
 * Hook for schedule lines bulk selection and bulk operations.
 * Mirrors the PO Lines usePOLinesBulkActions pattern.
 */
import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { purchasingAPI } from '../../../../lib/api';
import type { POLineSchedule } from '../../../../lib/api/types/purchasing';
import { useAuth } from '../../../../contexts/AuthContext';

interface BulkResultEntry {
  scheduleId: number;
  poNumber: string;
  lineNumber: number;
  error: string;
}

interface UseScheduleBulkActionsParams {
  schedules: POLineSchedule[];
  refresh: () => void;
}

export function useScheduleBulkActions({ schedules, refresh }: UseScheduleBulkActionsParams) {
  const { user } = useAuth();

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkAccepting, setBulkAccepting] = useState(false);
  const [bulkRejecting, setBulkRejecting] = useState(false);
  const [bulkHolding, setBulkHolding] = useState(false);
  const [showBulkAcceptModal, setShowBulkAcceptModal] = useState(false);
  const [showBulkRejectModal, setShowBulkRejectModal] = useState(false);
  const [showBulkHoldModal, setShowBulkHoldModal] = useState(false);
  const [bulkResultLog, setBulkResultLog] = useState<BulkResultEntry[]>([]);
  const [showBulkResultLog, setShowBulkResultLog] = useState(false);

  /** Check whether a schedule line can be selected for bulk actions. */
  const canSelect = useCallback((s: POLineSchedule): boolean => {
    if (!user || !user.platform) return false;
    if (s.status === 'cancelled' || s.on_hold) return false;

    const isSupplier = user.platform === 'SUPPLIER' || user.platform === 'HELIX';
    const isCustomer = user.platform === 'CUSTOMER';

    if (isSupplier && s.waiting_on === 'Supplier') return true;
    if (isCustomer && s.waiting_on === 'Customer') return true;

    return false;
  }, [user]);

  const getSelectableSchedules = useCallback(() => {
    return schedules.filter((s) => canSelect(s));
  }, [schedules, canSelect]);

  const handleSelectAll = useCallback(() => {
    const selectable = getSelectableSchedules();
    if (selectedIds.size === selectable.length && selectable.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectable.map((s) => s.id)));
    }
  }, [getSelectableSchedules, selectedIds.size]);

  const handleToggleSelect = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleBulkAccept = useCallback(async () => {
    if (selectedIds.size === 0) return;

    setBulkAccepting(true);
    let successCount = 0;
    const failedItems: BulkResultEntry[] = [];
    const schedMap = new Map(schedules.map((s) => [s.id, s]));

    for (const id of Array.from(selectedIds)) {
      try {
        await purchasingAPI.acceptScheduleDate(id, {});
        successCount++;
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { message?: string } } };
        const errorMsg = axiosErr?.response?.data?.message || (err instanceof Error ? err.message : 'Unknown error');
        const info = schedMap.get(id);
        failedItems.push({
          scheduleId: id,
          poNumber: info?.po_number || String(id),
          lineNumber: info?.line_number || 0,
          error: errorMsg,
        });
      }
    }

    setBulkAccepting(false);

    if (successCount > 0) {
      toast.success(`Accepted ${successCount} schedule${successCount > 1 ? 's' : ''} successfully`);
    }
    if (failedItems.length > 0) {
      setSelectedIds(new Set(failedItems.map((f) => f.scheduleId)));
      setBulkResultLog(failedItems);
      setShowBulkResultLog(true);
      toast.error(`Failed to accept ${failedItems.length} schedule${failedItems.length > 1 ? 's' : ''}`);
    } else {
      setSelectedIds(new Set());
    }

    setShowBulkAcceptModal(false);
    refresh();
  }, [selectedIds, schedules, refresh]);

  const handleBulkReject = useCallback(async (reason: string) => {
    if (selectedIds.size === 0 || !reason) return;

    setBulkRejecting(true);
    let successCount = 0;
    const failedItems: BulkResultEntry[] = [];
    const schedMap = new Map(schedules.map((s) => [s.id, s]));

    for (const id of Array.from(selectedIds)) {
      try {
        await purchasingAPI.declineScheduleDate(id, { reason });
        successCount++;
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { message?: string } } };
        const errorMsg = axiosErr?.response?.data?.message || (err instanceof Error ? err.message : 'Unknown error');
        const info = schedMap.get(id);
        failedItems.push({
          scheduleId: id,
          poNumber: info?.po_number || String(id),
          lineNumber: info?.line_number || 0,
          error: errorMsg,
        });
      }
    }

    setBulkRejecting(false);
    setShowBulkRejectModal(false);

    if (successCount > 0) {
      toast.success(`Rejected ${successCount} schedule${successCount > 1 ? 's' : ''} successfully`);
    }
    if (failedItems.length > 0) {
      setSelectedIds(new Set(failedItems.map((f) => f.scheduleId)));
      setBulkResultLog(failedItems);
      setShowBulkResultLog(true);
      toast.error(`Failed to reject ${failedItems.length} schedule${failedItems.length > 1 ? 's' : ''}`);
    } else {
      setSelectedIds(new Set());
    }

    refresh();
  }, [selectedIds, schedules, refresh]);

  const handleBulkPutOnHold = useCallback(async (reason: string) => {
    if (selectedIds.size === 0 || !reason) return;

    setBulkHolding(true);
    let successCount = 0;
    let failCount = 0;

    for (const id of Array.from(selectedIds)) {
      try {
        await purchasingAPI.putScheduleOnHold(id, { reason });
        successCount++;
      } catch (err) {
        failCount++;
        console.error(`Failed to put schedule ${id} on hold:`, err);
      }
    }

    setBulkHolding(false);
    setSelectedIds(new Set());
    setShowBulkHoldModal(false);

    if (successCount > 0) {
      toast.success(`Put ${successCount} schedule${successCount > 1 ? 's' : ''} on hold`);
    }
    if (failCount > 0) {
      toast.error(`Failed to put ${failCount} schedule${failCount > 1 ? 's' : ''} on hold`);
    }

    refresh();
  }, [selectedIds, refresh]);

  return {
    selectedIds,
    setSelectedIds,
    bulkAccepting,
    bulkRejecting,
    bulkHolding,
    showBulkAcceptModal,
    setShowBulkAcceptModal,
    showBulkRejectModal,
    setShowBulkRejectModal,
    showBulkHoldModal,
    setShowBulkHoldModal,
    bulkResultLog,
    showBulkResultLog,
    setShowBulkResultLog,
    canSelect,
    getSelectableSchedules,
    handleSelectAll,
    handleToggleSelect,
    handleBulkAccept,
    handleBulkReject,
    handleBulkPutOnHold,
  };
}
